import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AcousticRoomSignal } from '../lib/liveLessonTypes';
import { ACOUSTIC_DEFAULTS } from '../lib/acoustic/config';
import { SpeechModeAcousticEngine } from '../lib/acoustic/engine';
import { VadCapture } from '../lib/acoustic/vad/vadCapture';

type UseAcousticRoomAnalyserOptions = {
  enabled: boolean;
};

export function useAcousticRoomAnalyser(options: UseAcousticRoomAnalyserOptions) {
  const { enabled } = options;
  const [signal, setSignal] = useState<AcousticRoomSignal | null>(null);
  const [history, setHistory] = useState<AcousticRoomSignal[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureRef = useRef<VadCapture | null>(null);
  const engineRef = useRef<SpeechModeAcousticEngine | null>(null);

  const pushSignal = useCallback((nextSignal: AcousticRoomSignal) => {
    setSignal(nextSignal);
    setHistory((prev) => [...prev.slice(-(ACOUSTIC_DEFAULTS.historyLimit - 1)), nextSignal]);
  }, []);

  const stop = useCallback(() => {
    void captureRef.current?.stop();
    captureRef.current = null;
    engineRef.current?.setSignalListener(null);
    engineRef.current?.reset();
    engineRef.current = null;
    setIsActive(false);
    setIsLoadingModels(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      stop();
      setHistory([]);
      setSignal(null);
      return;
    }

    let mounted = true;
    const start = async () => {
      try {
        setIsLoadingModels(true);
        const engine = new SpeechModeAcousticEngine();
        engine.setSignalListener((nextSignal) => {
          if (!mounted) return;
          pushSignal(nextSignal);
        });
        await engine.initialize();

        if (!mounted) return;

        const capture = new VadCapture({
          callbacks: {
            onFrameProcessed: (probabilities, frame) => {
              const isSpeech = probabilities.isSpeech >= ACOUSTIC_DEFAULTS.vadPositiveThreshold;
              engine.processVadFrame(isSpeech, frame);
            },
            onSpeechEnd: (audio) => {
              void engine.processSpeechSegment(audio);
            }
          }
        });

        await capture.start();
        if (!mounted) {
          await capture.stop();
          return;
        }

        engineRef.current = engine;
        captureRef.current = capture;
        setError(null);
        setIsActive(true);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Mikrofon konnte nicht aktiviert werden';
        setError(message);
        stop();
      } finally {
        if (mounted) setIsLoadingModels(false);
      }
    };

    void start();
    return () => {
      mounted = false;
      stop();
    };
  }, [enabled, pushSignal, stop]);

  return useMemo(
    () => ({
      signal,
      history,
      isActive,
      isLoadingModels,
      error
    }),
    [signal, history, isActive, isLoadingModels, error]
  );
}
